import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Copy, Gift, Users } from 'lucide-react'
import { api } from '../../api/client'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { ShareButton } from '../ui/ShareButton'

interface ReferralResponse {
  code: string
  link: string
  invitedCount: number
  daysEarned: number
  bonusDays: number
}

/**
 * Compact referral widget on /profile. Shows the headline ("invite a
 * friend, both get N days") + a single stat line. Tap opens the share
 * modal — most users only need the modal occasionally, so we keep the
 * profile surface uncluttered.
 */
export function ReferralCard() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const referralQ = useQuery({
    queryKey: ['me', 'referral'],
    queryFn: () => api.getReferral(),
    staleTime: 60_000,
  })

  const r = referralQ.data
  if (referralQ.isLoading || !r) {
    return <div className="h-14 animate-pulse rounded-2xl bg-sage-100/60" />
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-2xl bg-white/70 p-3 text-left ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
      >
        <img
          src="/icons/profile-referral.png"
          alt=""
          aria-hidden="true"
          className="h-10 w-10 shrink-0 select-none"
          draggable={false}
        />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-sm text-sage-800 leading-snug">
            {t('referral.compact_title', { days: r.bonusDays })}
          </p>
          <p className="mt-0.5 text-xs text-sage-500">
            {t('referral.compact_stats', { invited: r.invitedCount, earned: r.daysEarned })}
          </p>
        </div>
        <Gift className="h-4 w-4 shrink-0 text-copper" strokeWidth={1.8} aria-hidden />
      </button>

      <ReferralModal open={open} onClose={() => setOpen(false)} data={r} />
    </>
  )
}

function ReferralModal({
  open,
  onClose,
  data: r,
}: {
  open: boolean
  onClose: () => void
  data: ReferralResponse
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(r.link)
      setCopied(true)
      toast.success(t('referral.copied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('referral.copy_failed'))
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('referral.title')}>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <img
            src="/icons/profile-referral.png"
            alt=""
            aria-hidden="true"
            className="h-12 w-12 shrink-0 select-none"
            draggable={false}
          />
          <p className="text-sm text-sage-700 leading-relaxed">
            {t('referral.subtitle', { days: r.bonusDays })}
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-sage-200">
          <span className="flex-1 truncate font-mono text-xs text-sage-700">{r.link}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-sage-800 hover:bg-sage-100"
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
            {copied ? t('referral.copied_short') : t('referral.copy')}
          </button>
        </div>

        <ShareButton
          className="w-full"
          url={r.link}
          text={t('referral.share_text', { days: r.bonusDays })}
          label={t('referral.share_button')}
          size="md"
        />

        <div className="flex flex-col gap-2 rounded-xl bg-sage-50 px-3 py-2 text-sm">
          <div className="flex items-center gap-2 text-sage-700">
            <Users className="h-4 w-4 shrink-0" strokeWidth={1.8} aria-hidden />
            <span>{t('referral.stats_invited', { count: r.invitedCount })}</span>
          </div>
          <div className="font-medium text-amber-700">
            {t('referral.stats_earned', { days: r.daysEarned })}
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** Tiny placeholder used by clients that don't have the new API yet. */
export function ReferralCardFallback() {
  return null
}

/** Re-exported to keep the import surface tidy. */
export type { ReferralResponse }

/** Convenience wrapper exposing the static Button variant for future use. */
export function ReferralCopyButton({ link }: { link: string }) {
  const { t } = useTranslation()
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(link)
        toast.success(t('referral.copied'))
      }}
    >
      {t('referral.copy')}
    </Button>
  )
}
